import { setTransactionMetadata } from "@rea-argonaut/observability";
import {
  ForcedExperiments,
  ForcedFeatures,
  OptimizelyClient,
  User,
  UserContext,
} from "../types";
import { createUserContext } from "./createUserContext";
import getForcedExperiments from "./getForcedExperiments";
import getForcedFeatures from "./getForcedFeatures";
import getUserAttributes from "./getUserAttributes";
import { OptimizelyDecision } from "@optimizely/optimizely-sdk";

class OptimizelyManager<Variables = { [variableKey: string]: unknown }> {
  optimizelyClient: OptimizelyClient;
  user: User;
  isFeatureExperimentation: boolean;
  userContext: UserContext;
  forcedExperiments: ForcedExperiments;
  forcedFeatures: ForcedFeatures;

  constructor(
    optimizelyClient: OptimizelyClient,
    user: User,
    queryParams: URLSearchParams,
    isFeatureExperimentation = true,
  ) {
    this.optimizelyClient = optimizelyClient;
    this.user = { ...user, userId: user.lockeId ?? user.userId };
    this.isFeatureExperimentation = isFeatureExperimentation;

    this.userContext = createUserContext({
      isFeatureExperimentation,
      optimizelyClient,
      user: this.user,
    });

    this.forcedExperiments = getForcedExperiments(
      this.optimizelyClient,
      queryParams,
      this.isFeatureExperimentation,
    );
    this.forceExperimentVariations();

    this.forcedFeatures = getForcedFeatures(this.optimizelyClient, queryParams);
  }

  private isServerSide = (): boolean => {
    return typeof window === "undefined";
  };

  private forceExperimentVariations = (): void => {
    this.forcedExperiments.forEach((experiment) => {
      if (!this.user.userId) return;

      void (this.isFeatureExperimentation
        ? this.userContext.setForcedDecision(
            { flagKey: experiment.experimentId },
            { variationKey: experiment.variation },
          )
        : this.optimizelyClient.setForcedVariation(
            experiment.experimentId,
            this.user.userId,
            experiment.variation,
          ));
    });
  };

  private setExperimentTransactionMetadata = (
    experimentId: string,
    variation: string | null,
  ) => {
    if (variation) {
      setTransactionMetadata({
        splunkKey: `experiment_${experimentId}`,
        newRelicKey: `experiment_${experimentId}`,
        value: variation,
      });
    }
  };

  private setFeatureTransactionMetadata = (
    featureId: string,
    featureVariation: string | null,
  ) => {
    if (featureVariation) {
      setTransactionMetadata({
        splunkKey: `feature_${featureId}`,
        newRelicKey: `feature_${featureId}`,
        value: featureVariation,
      });
    }
  };

  private makeDecision = (id: string): OptimizelyDecision | null => {
    return this.userContext.decide(id);
  };

  updateUserContext = (customUserAttributes: Record<string, unknown>): void => {
    if (!this.user.userId || !this.isFeatureExperimentation) return;

    this.userContext = createUserContext({
      isFeatureExperimentation: this.isFeatureExperimentation,
      optimizelyClient: this.optimizelyClient,
      user: this.user,
      customUserAttributes,
    });
    this.forceExperimentVariations();
  };

  getVariation = (
    experimentId: string,
    customUserAttributes?: Record<string, unknown>,
  ): string | null => {
    if (!this.user.userId) return null;

    if (this.isFeatureExperimentation) {
      void (
        customUserAttributes && this.updateUserContext(customUserAttributes)
      );
      const variation = this.makeDecision(experimentId)?.variationKey ?? null;

      this.setExperimentTransactionMetadata(experimentId, variation);

      return variation;
    }

    const variation = this.optimizelyClient.getVariation(
      experimentId,
      this.user.userId,
      {
        ...getUserAttributes(this.user),
        ...customUserAttributes,
      },
    );

    this.setExperimentTransactionMetadata(experimentId, variation);

    return variation;
  };

  getFeatureVariation = (
    featureId: string,
    customUserAttributes?: Record<string, unknown>,
  ): string | null => {
    if (!this.user.userId) return null;

    const forcedFeatureVariation = this.forcedFeatures.find((feature) => {
      return feature.featureId === featureId;
    });

    if (forcedFeatureVariation) {
      const featureVariation = forcedFeatureVariation.variation;

      this.setFeatureTransactionMetadata(featureId, featureVariation);

      return featureVariation;
    }

    if (this.isFeatureExperimentation) {
      void (
        customUserAttributes && this.updateUserContext(customUserAttributes)
      );
      const featureVariation =
        this.makeDecision(featureId)?.variationKey ?? null;

      this.setFeatureTransactionMetadata(featureId, featureVariation);

      return featureVariation;
    }

    const featureEnabled = this.optimizelyClient.isFeatureEnabled(
      featureId,
      this.user.userId,
      {
        ...getUserAttributes(this.user),
        ...customUserAttributes,
      },
    );

    const featureVariation = featureEnabled ? "on" : "off";

    this.setFeatureTransactionMetadata(featureId, featureVariation);

    return featureVariation;
  };

  isFeatureEnabled = (
    featureId: string,
    customUserAttributes?: Record<string, unknown>,
  ) => {
    return (
      this.getFeatureVariation(
        featureId,
        customUserAttributes,
      )?.toLowerCase() === "on"
    );
  };

  getVariables = (id: string): Variables | null => {
    if (!this.user.userId) return null;

    if (this.isFeatureExperimentation) {
      return (this.makeDecision(id)?.variables as Variables) ?? null;
    }
    return this.optimizelyClient.getAllFeatureVariables(
      id,
      this.user.userId,
    ) as Variables;
  };

  activate = (
    experimentId: string,
    customUserAttributes?: Record<string, unknown>,
  ): void => {
    if (this.isServerSide()) {
      return;
    }
    if (!this.user.userId) return;

    if (this.isFeatureExperimentation) {
      void (
        customUserAttributes && this.updateUserContext(customUserAttributes)
      );
      this.makeDecision(experimentId);
      return;
    }

    this.optimizelyClient.activate(experimentId, this.user.userId, {
      ...getUserAttributes(this.user),
      ...customUserAttributes,
    });
  };

  track = (
    eventKey: string,
    customUserAttributes?: Record<string, unknown>,
  ): void => {
    if (this.isServerSide()) {
      return;
    }

    if (!this.user.userId) return;

    if (this.isFeatureExperimentation) {
      void (
        customUserAttributes && this.updateUserContext(customUserAttributes)
      );
      this.userContext.trackEvent(eventKey);
      return;
    }

    this.optimizelyClient.track(eventKey, this.user.userId, {
      ...getUserAttributes(this.user),
      ...customUserAttributes,
    });
  };
}

export default OptimizelyManager;
